import React, { useState, useEffect } from 'react';
import FeatureGateService from '../services/featureGateService';
import './FeatureGateManagementPage.css';

const FeatureGateManagementPage = () => {
  const [featureConfigs, setFeatureConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchIboNumber, setSearchIboNumber] = useState('');
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [usersWithFeature, setUsersWithFeature] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Load feature configs on mount
  useEffect(() => {
    loadFeatureConfigs();
  }, []);

  const loadFeatureConfigs = async () => {
    try {
      setLoading(true);
      const response = await FeatureGateService.getAllFeatureConfigs();
      if (response.status === 'success' && response.data) {
        setFeatureConfigs(response.data);
      }
    } catch (error) {
      console.error('Failed to load feature configs:', error);
      showMessage('error', 'Failed to load feature configurations');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleToggleGlobal = (featureCode, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'enable' : 'disable';
    
    setConfirmAction({
      type: 'global',
      featureCode,
      enabled: newStatus,
      message: `Are you sure you want to ${action} "${featureCode}" for ALL users?`
    });
    setShowConfirmDialog(true);
  };

  const handleBucketUser = (featureCode, enabled) => {
    if (!searchIboNumber.trim()) {
      showMessage('error', 'Please enter an IBO number');
      return;
    }

    const action = enabled ? 'enable' : 'disable';
    setConfirmAction({
      type: 'individual',
      featureCode,
      iboNumber: searchIboNumber.trim(),
      enabled,
      message: `${action.charAt(0).toUpperCase() + action.slice(1)} "${featureCode}" for IBO ${searchIboNumber}?`
    });
    setShowConfirmDialog(true);
  };

  const executeConfirmAction = async () => {
    try {
      if (confirmAction.type === 'global') {
        const enabledFlag = confirmAction.enabled ? 1 : 0;
        await FeatureGateService.toggleGlobalFeature(confirmAction.featureCode, enabledFlag);
        showMessage('success', `Feature "${confirmAction.featureCode}" ${confirmAction.enabled ? 'enabled' : 'disabled'} globally`);
        await loadFeatureConfigs();
      } else if (confirmAction.type === 'individual') {
        const enabledFlag = confirmAction.enabled ? 1 : 0;
        await FeatureGateService.updateFeatureGate(
          confirmAction.featureCode,
          enabledFlag,
          confirmAction.iboNumber
        );
        showMessage('success', `Feature "${confirmAction.featureCode}" ${confirmAction.enabled ? 'enabled' : 'disabled'} for IBO ${confirmAction.iboNumber}`);
        await loadFeatureConfigs();
      }
    } catch (error) {
      console.error('Failed to execute action:', error);
      showMessage('error', error.message || 'Failed to update feature gate');
    } finally {
      setShowConfirmDialog(false);
      setConfirmAction(null);
    }
  };

  const loadUsersWithFeature = async (featureCode) => {
    try {
      setSelectedFeature(featureCode);
      const response = await FeatureGateService.getUsersWithFeature(featureCode);
      if (response.status === 'success' && response.data) {
        setUsersWithFeature(response.data);
      }
    } catch (error) {
      console.error('Failed to load users with feature:', error);
      showMessage('error', 'Failed to load users with feature');
    }
  };

  if (loading) {
    return (
      <div className="feature-gate-page">
        <div className="loading">Loading feature configurations...</div>
      </div>
    );
  }

  return (
    <div className="feature-gate-page">
      <h1>Feature Gate Management</h1>

      {/* Message Display */}
      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Section 1: Global Feature Controls */}
      <section className="section global-controls">
        <h2>Global Feature Controls (100% Rollout)</h2>
        <div className="feature-table">
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Description</th>
                <th>Global Status</th>
                <th>Individual Users</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {featureConfigs.map((config) => (
                <tr key={config.featureCode}>
                  <td className="feature-name">{config.featureName}</td>
                  <td className="feature-description">{config.description}</td>
                  <td className="feature-status">
                    {config.enabledGlobally ? (
                      <span className="status-badge status-enabled">Enabled Globally</span>
                    ) : (
                      <span className="status-badge status-disabled">Disabled</span>
                    )}
                  </td>
                  <td className="user-count">
                    <button
                      className="link-button"
                      onClick={() => loadUsersWithFeature(config.featureCode)}
                    >
                      {config.totalUsersEnabled} users
                    </button>
                  </td>
                  <td className="actions">
                    <button
                      className={`toggle-button ${config.enabledGlobally ? 'enabled' : 'disabled'}`}
                      onClick={() => handleToggleGlobal(config.featureCode, config.enabledGlobally)}
                    >
                      {config.enabledGlobally ? 'Disable Globally' : 'Enable Globally'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 2: Individual User Management */}
      <section className="section user-management">
        <h2>Individual User Management</h2>
        <div className="search-section">
          <input
            type="text"
            className="search-input"
            placeholder="Enter IBO Number"
            value={searchIboNumber}
            onChange={(e) => setSearchIboNumber(e.target.value)}
          />
        </div>

        <div className="feature-list">
          {featureConfigs.map((config) => (
            <div key={config.featureCode} className="feature-item">
              <div className="feature-info">
                <h3>{config.featureName}</h3>
                <p>{config.description}</p>
                {config.enabledGlobally && (
                  <span className="global-badge">Enabled Globally</span>
                )}
              </div>
              <div className="feature-actions">
                <button
                  className="bucket-button bucket-in"
                  onClick={() => handleBucketUser(config.featureCode, true)}
                  disabled={config.enabledGlobally}
                  title={config.enabledGlobally ? 'Feature is enabled globally' : 'Enable for this user'}
                >
                  Bucket In
                </button>
                <button
                  className="bucket-button bucket-out"
                  onClick={() => handleBucketUser(config.featureCode, false)}
                  disabled={config.enabledGlobally}
                  title={config.enabledGlobally ? 'Feature is enabled globally' : 'Disable for this user'}
                >
                  Bucket Out
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section 3: Feature Statistics */}
      {selectedFeature && (
        <section className="section feature-statistics">
          <h2>Users with "{selectedFeature}" Feature</h2>
          <button
            className="close-button"
            onClick={() => setSelectedFeature(null)}
          >
            Close
          </button>
          <div className="user-list">
            {usersWithFeature.length > 0 ? (
              <ul>
                {usersWithFeature.map((iboNumber) => (
                  <li key={iboNumber}>{iboNumber}</li>
                ))}
              </ul>
            ) : (
              <p className="no-users">No users have this feature enabled individually</p>
            )}
          </div>
        </section>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmAction && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Action</h3>
            <p>{confirmAction.message}</p>
            <div className="modal-actions">
              <button
                className="confirm-button"
                onClick={executeConfirmAction}
              >
                Confirm
              </button>
              <button
                className="cancel-button"
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmAction(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureGateManagementPage;
